// storage.js

// ---- tiny LRU + memo --------------------------------------------------------
export class LRU {
    constructor(limit = 256) {
        this.limit = limit
        this.map = new Map()
    }
    get(k) {
        if (!this.map.has(k)) return undefined
        const v = this.map.get(k)
        this.map.delete(k)
        this.map.set(k, v)
        return v
    }
    set(k, v) {
        if (this.map.has(k)) this.map.delete(k)
        this.map.set(k, v)
        if (this.map.size > this.limit) this.map.delete(this.map.keys().next().value)
        return v
    }
    has(k) {
        return this.map.has(k)
    }
    delete(k) {
        return this.map.delete(k)
    }
    clear() {
        this.map.clear()
    }
}
export const memoiseLRU = (fn, limit = 256) => {
    const cache = new LRU(limit)
    return arg => {
        const key = String(arg)
        const hit = cache.get(key)
        if (hit !== undefined) return hit
        const val = fn(arg)
        cache.set(key, val)
        return val
    }
}

// ---- key encoding (new + legacy-compat) -------------------------------------
export const normaliseKey = key => encodeURIComponent(String(key).trim())

// ---- legacy key encoding ----------------------------------------------------
const LEGACY_UNSAFE = /[\/\s"]/g
// legacy: lower-case, '/'→'.' (drop very first), strip quotes/whitespace, drop a single leading '.', then URI-encode
export const legacyEncodeKey = key => {
    let out = String(key)
        .toLowerCase()
        .replace(LEGACY_UNSAFE, (ch, i) => (ch === '/' ? (i === 0 ? '' : '.') : ''))
    if (out.startsWith('.')) out = out.slice(1)
    return encodeURIComponent(out)
}
const memoLegacyEncodeKey = memoiseLRU(legacyEncodeKey, 512)
// ---- JSON helpers -----------------------------------------------------------
const safeParse = str => {
    try {
        return JSON.parse(str)
    } catch {
        return str
    }
}
// ---- deepEqual helpers -----------------------------------------------------------
const stableStringify = v =>
    JSON.stringify(v, (k, val) =>
        val && typeof val === 'object' && !Array.isArray(val)
            ? Object.keys(val)
                  .sort()
                  .reduce((o, k2) => ((o[k2] = val[k2]), o), {})
            : val,
    )

// ---- deepEqual helpers -----------------------------------------------------------
const deepEqual = (a, b) => {
    if (Object.is(a, b)) return true
    if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false
    try {
        return stableStringify(a) === stableStringify(b)
    } catch {
        return false
    }
}
// ---- deepEqual helpers -----------------------------------------------------------
const isPlainObj = o => o && (typeof o === 'object' || typeof o === 'number') && !Array.isArray(o)

const isPrimitive = v => (v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') && !Array.isArray(v)

const toStorage = v => (typeof v === 'string' || typeof v === 'number' ? v : JSON.stringify(v))
// ---- deepEqual helpers -----------------------------------------------------------
const deepMerge = (t, s) => {
    if (Array.isArray(t) && Array.isArray(s)) return s.slice()
    if (isPlainObj(t) && isPlainObj(s)) {
        const out = { ...t }
        for (const [k, v] of Object.entries(s)) out[k] = deepMerge(out[k], v)
        return out
    }
    return s
}

// ---- storage resolver --------------------------------------------------------
const resolveStore = (storageType = 'localStorage') => {
    const win = typeof window !== 'undefined' ? window : undefined
    const store = storageType === 'sessionStorage' ? win?.sessionStorage : win?.localStorage
    if (!store) throw new Error('Web Storage is unavailable in this environment.')
    return store
}

// ---- generic get storage/data/value from browsers storage APIs -------------------------------------------------------------
export const getStorage = (storageType = 'localStorage', key, { raw = false, migrate = true } = {}) => {
    try {
        const store = resolveStore(storageType)
        const k = normaliseKey(key)

        let v = store.getItem(k)
        if (v != null && v !== 'undefined') return raw ? v : safeParse(v)

        // try legacy key variant
        const legacyK = memoLegacyEncodeKey(key)
        if (legacyK !== k) {
            const lv = store.getItem(legacyK)
            if (lv != null && lv !== 'undefined') {
                const typed = safeParse(lv)
                if (migrate) {
                    try {
                        store.setItem(k, JSON.stringify(typed))
                        store.removeItem(legacyK)
                    } catch {}
                }
                return raw ? lv : typed
            }
        }
        return null
    } catch (err) {
        console.error('getStorage error:', err)
        return null
    }
}

// ---- generic set storage/data/value to browsers storage APIs -------------------------------------------------------------
export const setStorage = async (storageType = 'localStorage', key, value, { skipIfUnchanged = false } = {}) => {
    try {
        const store = resolveStore(storageType)
        const k = normaliseKey(key)

        if (value === undefined) {
            store.removeItem(k)
            return
        }

        if (skipIfUnchanged) {
            const cur = await getStorage(storageType, key)
            if (deepEqual(cur, value)) return
        }
        // RAW for strings; JSON for everything else
        store.setItem(k, toStorage(value)) // <-- strings raw, others JSON
    } catch (err) {
        console.error('setStorage error:', err)
    }
}

// ---- generic update storage/data/value to browsers storage APIs -------------------------------------------------------------
export const updateStorage = async (storageType = 'localStorage', key, patch) => {
    try {
        const store = resolveStore(storageType)
        const k = normaliseKey(key)
        const cur = await getStorage(storageType, key)

        const next =
            cur && typeof cur === 'object' && !Array.isArray(cur) && patch && typeof patch === 'object' && !Array.isArray(patch)
                ? (() => {
                      const o = { ...cur }
                      for (const p in patch) o[p] = patch[p]
                      return o
                  })()
                : patch

        if (next === undefined) {
            store.removeItem(k)
            return
        }
        store.setItem(k, toStorage(next)) // <-- strings raw, others JSON
    } catch (err) {
        console.error('updateStorage error:', err)
    }
}

// ---- generic remove storage/data/value from browsers storage APIs -------------------------------------------------------------
export const removeStorage = (storageType = 'localStorage', keys) => {
    try {
        const store = resolveStore(storageType)
        const list = Array.isArray(keys) ? keys : [keys]
        const uniq = [...new Set(list.filter(k => k != null && k !== '').map(normaliseKey))]
        for (const k of uniq) store.removeItem(k)
    } catch (err) {
        console.error('removeStorage error:', err)
    }
}

// substring/bulk removal (covers raw, normalised, and legacy-encoded substrings)
export const removeStorageBySubstring = (storageType = 'localStorage', substrings = []) => {
    if (!Array.isArray(substrings) || substrings.length === 0) return
    try {
        const store = resolveStore(storageType)
        const pats = new Set()
        for (const s of substrings) {
            if (s == null || s === '') continue
            pats.add(String(s))
            pats.add(normaliseKey(s))
            pats.add(memoLegacyEncodeKey(s))
        }
        // iterate snapshot of keys to avoid live length changes
        const keys = []
        for (let i = 0; i < store.length; i++) keys.push(store.key(i))
        const patsArray = [...pats]
        for (const k of keys) {
            if (patsArray.some(p => k.includes(p))) store.removeItem(k)
        }
    } catch (err) {
        console.error('removeStorageBySubstring error:', err)
    }
}

// optional: normalise existing values to canonical JSON for a storage
export const migrateStorageValues = (storageType = 'localStorage') => {
    try {
        const store = resolveStore(storageType)
        const keys = []
        for (let i = 0; i < store.length; i++) keys.push(store.key(i))
        for (const k of keys) {
            const v = store.getItem(k)
            if (v == null || v === 'undefined') {
                store.removeItem(k)
                continue
            }
            try {
                store.setItem(k, JSON.stringify(JSON.parse(v)))
            } catch {
                /* keep legacy strings */
            }
        }
    } catch (err) {
        console.error('migrateStorageValues error:', err)
    }
}

// ---- thin convenience wrappers (optional) -----------------------------------
export const getLocalStorage = (key, opts) => getStorage('localStorage', key, opts)
export const setLocalStorage = (key, val, opts) => setStorage('localStorage', key, val, opts)
export const updateLocalStorage = (key, patch) => updateStorage('localStorage', key, patch)
export const removeLocalStorage = keys => removeStorage('localStorage', keys)
export const removeLocalBySub = subs => removeStorageBySubstring('localStorage', subs)

export const getSessionStorage = (key, opts) => getStorage('sessionStorage', key, opts)
export const setSessionStorage = (key, val, opts) => setStorage('sessionStorage', key, val, opts)
export const updateSessionStorage = (key, patch) => updateStorage('sessionStorage', key, patch)
export const removeSessionStorage = keys => removeStorage('sessionStorage', keys)
export const delSessionStorage = keys => removeStorage('sessionStorage', keys)
export const removeSessionBySub = subs => removeStorageBySubstring('sessionStorage', subs)

// --- Cookies ---------------------------------------------------------------

/**
 * Remove cookies whose names match any of the given substrings.
 * Note: HttpOnly cookies cannot be removed via JS.
 * @param {string[]} substrings
 * @param {{exact?: boolean, domains?: string[], paths?: string[]}} [opts]
 */
export function removeCookies(substrings, opts = {}) {
    const { exact = false, domains, paths } = opts
    if (!Array.isArray(substrings) || substrings.length === 0) return
    if (typeof document === 'undefined' || typeof document.cookie !== 'string') return

    const safeDecode = s => {
        try {
            return decodeURIComponent(s)
        } catch {
            return s
        }
    }
    const cookiePairs = document.cookie ? document.cookie.split(/;\s*/).filter(Boolean) : []

    // Collect names to remove (parse once)
    const names = []
    for (const pair of cookiePairs) {
        const eq = pair.indexOf('=')
        const rawName = eq === -1 ? pair : pair.slice(0, eq)
        const name = safeDecode(rawName.trim())
        if (!name) continue
        const hit = exact ? substrings.includes(name) : substrings.some(sub => String(name).includes(sub))
        if (hit) names.push(name)
    }
    if (names.length === 0) return

    const host = (typeof location !== 'undefined' && location.hostname) || ''
    const path = (typeof location !== 'undefined' && location.pathname) || '/'

    // Candidate domains: host-only (''), current host, and parent domains.
    const domainCandidates =
        Array.isArray(domains) && domains.length
            ? domains.slice()
            : (() => {
                  if (!host || host === 'localhost') return [''] // host-only only
                  const parts = host.split('.')
                  const out = new Set(['']) // '' => no Domain attribute (host-only)
                  for (let i = 0; i < parts.length - 1; i++) {
                      out.add(parts.slice(i).join('.'))
                  }
                  return [...out]
              })()

    // Candidate paths: provided or every parent of current path.
    const pathCandidates =
        Array.isArray(paths) && paths.length
            ? paths.slice()
            : (() => {
                  const segs = path.split('/').filter(Boolean)
                  const acc = ['/']
                  let cur = ''
                  for (const s of segs) {
                      cur += `/${s}`
                      acc.push(cur)
                  }
                  // ensure unique, shortest first
                  return [...new Set(acc)]
              })()

    const expireStr = 'Thu, 01 Jan 1970 00:00:00 GMT'
    const setExpired = (name, p, d) => {
        // RFC-compliant removal; include both Expires and Max-Age for good measure.
        document.cookie = `${encodeURIComponent(name)}=` + `; Expires=${expireStr}` + `; Max-Age=0` + `; Path=${p || '/'}` + (d ? `; Domain=${d}` : '')
    }

    // Try all domain/path combos for each name.
    for (const name of names) {
        // host-only first (''), then parent domains
        for (const dom of domainCandidates) {
            for (const p of pathCandidates) {
                try {
                    setExpired(name, p, dom || undefined)
                } catch {}
            }
        }
    }
}

// --- IndexedDB -------------------------------------------------------------

/**
 * Remove items from an IndexedDB object store when the key (or value if enabled)
 * includes any of the given substrings.
 * @param {string} dbName
 * @param {string} storeName
 * @param {string[]} substrings
 * @param {{includeValue?: boolean}} [opts]
 * @returns {Promise<number>} count removed
 */
export async function removeIndexedDBItems(dbName, storeName, substrings, opts = {}) {
    const { includeValue = false } = opts
    if (!Array.isArray(substrings) || substrings.length === 0) return 0
    if (typeof indexedDB === 'undefined') return 0

    const openDB = name =>
        new Promise((resolve, reject) => {
            const req = indexedDB.open(name)
            req.onerror = () => reject(req.error || new Error('IndexedDB open error'))
            req.onsuccess = () => resolve(req.result)
        })

    const txDone = tx =>
        new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve()
            tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'))
            tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction error'))
        })

    let db
    let deleted = 0
    try {
        db = await openDB(dbName)
        if (!db.objectStoreNames.contains(storeName)) {
            console.warn(`removeIndexedDBItems: store "${storeName}" not found in "${dbName}"`)
            return 0
        }
        const tx = db.transaction(storeName, 'readwrite')
        const store = tx.objectStore(storeName)

        await new Promise((resolve, reject) => {
            const req = store.openCursor()
            req.onerror = () => reject(req.error || new Error('Cursor error'))
            req.onsuccess = e => {
                const cursor = e.target.result
                if (!cursor) return resolve()

                const keyStr = String(cursor.key)
                const matchKey = substrings.some(s => keyStr.includes(s))
                const matchVal = includeValue
                    ? (() => {
                          try {
                              return substrings.some(s => String(cursor.value).includes(s))
                          } catch {
                              return false
                          }
                      })()
                    : false

                if (matchKey || matchVal) {
                    try {
                        store.delete(cursor.key)
                        deleted += 1
                    } catch {}
                }
                cursor.continue()
            }
        })

        await txDone(tx)
    } catch (err) {
        console.error('removeIndexedDBItems error:', err)
    } finally {
        try {
            db && db.close && db.close()
        } catch {}
    }
    return deleted
}
